import { Avatar, CircularProgress, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import { getReadableShort } from '../../../utils/dates';
import type { ChatRoomNode, ChatRoomUser } from '../types';

interface ChatListProps {
  chatRooms: ChatRoomNode[];
  loading: boolean;
  currentUserId: string;
  onChatSelect: (chatRoom: ChatRoomNode) => void;
}

const ChatListContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const MessagesContainer = styled('div')(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  padding: `${theme.baseUnit * 2}px ${theme.baseUnit * 2}px 0`,
  maxHeight: 400,
  display: 'flex',
  flexDirection: 'column',
}));

const LoadingContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: 200,
});

const EmptyState = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: 200,
  padding: theme.baseUnit * 3,
  textAlign: 'center',
}));

const ChatListItem = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.baseUnit * 2,
  borderBottom: `1px solid ${theme.colors.gray[200]}`,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.colors.gray[50],
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.colors.alias.primary}`,
    outlineOffset: -2,
  },
  '&:last-child': {
    borderBottom: 'none',
  },
}));

const ChatListItemInfo = styled('div')(({ theme }) => ({
  marginLeft: theme.baseUnit,
  flex: 1,
  minWidth: 0,
}));

const ChatListItemName = styled(Typography)(({ theme }) => ({
  ...theme.typography.fontFamily.sharp600,
  fontSize: 14,
  color: theme.colors.gray[800],
}));

const ChatListItemLastMessage = styled(Typography)(({ theme }) => ({
  ...theme.typography.fontFamily.reader400,
  fontSize: 12,
  color: theme.colors.gray[600],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const ChatListItemTime = styled(Typography)(({ theme }) => ({
  ...theme.typography.fontFamily.reader400,
  fontSize: 11,
  color: theme.colors.gray[500],
  marginLeft: theme.baseUnit,
}));

const ChatListUnreadBadge = styled('div')(({ theme }) => ({
  backgroundColor: theme.colors.red[500],
  color: theme.colors.alias.textContrast,
  borderRadius: '50%',
  minWidth: 18,
  height: 18,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 10,
  fontWeight: 600,
  marginLeft: theme.baseUnit,
}));

const isImagePreview = (text: string) =>
  /\.(jpg|jpeg|png|gif|webp)$/i.test(text) || text.includes('amazonaws.com');

interface ChatListItemProps {
  chatRoom: ChatRoomNode;
  currentUserId: string;
  onClick: (chatRoom: ChatRoomNode) => void;
}

const ChatListItemComponent: React.FC<ChatListItemProps> = ({
  chatRoom,
  currentUserId,
  onClick,
}) => {
  const messages = chatRoom.messages?.nodes || [];
  const lastMessage = messages[messages.length - 1];
  const unreadCount = chatRoom.messages?.unreadMessageCount || 0;

  const otherUser = chatRoom.usersWithoutInput.nodes.find(
    (user: ChatRoomUser) => user.id !== currentUserId,
  );

  const getDisplayName = () => {
    if (chatRoom.kind === 'direct' && otherUser) {
      return `${otherUser.firstName} ${otherUser.lastName}`;
    }
    const firstOther = chatRoom.usersWithoutInput.nodes.find(
      (user: ChatRoomUser) => user.id !== currentUserId,
    );
    return firstOther
      ? `${firstOther.firstName} ${firstOther.lastName}`
      : chatRoom.name || 'Unknown Chat';
  };

  const getAvatar = () =>
    chatRoom.kind === 'direct' && otherUser
      ? (otherUser.avatar?.preview ?? undefined)
      : undefined;

  const getLastMessagePreview = () => {
    if (!lastMessage) {
      return 'No messages';
    }
    if (isImagePreview(lastMessage.text)) {
      return '📷 Photo';
    }
    return lastMessage.text.length > 30
      ? `${lastMessage.text.substring(0, 30)}...`
      : lastMessage.text;
  };

  const displayName = getDisplayName();

  return (
    <ChatListItem
      onClick={() => onClick(chatRoom)}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick(chatRoom);
        }
      }}
    >
      <Avatar
        src={getAvatar()}
        alt={displayName}
        sx={{ width: 40, height: 40 }}
      >
        {displayName.charAt(0)}
      </Avatar>
      <ChatListItemInfo>
        <ChatListItemName>{displayName}</ChatListItemName>
        <ChatListItemLastMessage>
          {getLastMessagePreview()}
        </ChatListItemLastMessage>
      </ChatListItemInfo>
      {lastMessage && (
        <ChatListItemTime>
          {getReadableShort(lastMessage.createdAt)}
        </ChatListItemTime>
      )}
      {unreadCount > 0 && (
        <ChatListUnreadBadge aria-hidden>
          {unreadCount > 99 ? '99+' : unreadCount}
        </ChatListUnreadBadge>
      )}
    </ChatListItem>
  );
};

const ChatList: React.FC<ChatListProps> = ({
  chatRooms,
  loading,
  currentUserId,
  onChatSelect,
}) => (
  <ChatListContainer>
    <MessagesContainer>
      {loading ? (
        <LoadingContainer>
          <CircularProgress size={40} />
        </LoadingContainer>
      ) : chatRooms.length === 0 ? (
        <EmptyState>
          <Typography variant="body2" color="textSecondary">
            No conversations yet
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Start chatting with your coach or team
          </Typography>
        </EmptyState>
      ) : (
        chatRooms.map((chatRoom) => (
          <ChatListItemComponent
            key={chatRoom.id}
            chatRoom={chatRoom}
            currentUserId={currentUserId}
            onClick={onChatSelect}
          />
        ))
      )}
    </MessagesContainer>
  </ChatListContainer>
);

export default ChatList;
